import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCalloutComponent } from '@ui/callout/callout.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxCardHeaderComponent } from '@ui/card/card-header.component';
import { VxCellDirective } from '@ui/data-table/cell.directive';
import { VxDataTableComponent } from '@ui/data-table/data-table.component';
import { VxHeaderDirective } from '@ui/data-table/header.directive';
import { VxColumn, VxPage } from '@ui/data-table/data-table.types';
import { VxRowActionsDirective } from '@ui/data-table/row-actions.directive';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { VxItemCellComponent } from '@ui/item-cell/item-cell.component';
import { VxMenuComponent } from '@ui/menu/menu.component';
import { VxMenuItemComponent } from '@ui/menu/menu-item.component';
import { VxMenuTriggerDirective } from '@ui/menu/menu-trigger.directive';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { VxStatusDotComponent } from '@ui/status-dot/status-dot.component';
import { VxTooltipDirective } from '@ui/tooltip/tooltip.directive';
import { CopyValueComponent } from 'src/app/core/components/copy-value/copy-value.component';
import { DateAgoPipe } from 'src/app/core/pipes/date-ago.pipe';
import { SharedModule } from 'src/app/core/shared.module';
import { Alert, AlertSourceType } from 'src/app/models/alert';
import { AlertStatusLabelComponent } from 'src/app/pages/internal/alerts/components/alert-status-label/alert-status-label.component';
import { BreadcrumbService } from 'src/app/services/breadcrumb/breadcrumb.service';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import {
  DeleteAlertSourceAction,
  LoadAlertSourceAlertsAction,
  LoadAlertSourceDetailsAction,
} from 'src/app/store/alerts/alerts.actions';
import { AlertsState } from 'src/app/store/alerts/alerts.state';
import { SortEntities } from 'src/app/store/common/helpers';
import { alertSourceTypeVM } from '../../alert-source-types';
import { ConfirmService } from 'src/app/services/dialog/confirm.service';

const DEFAULT_PAGE_SIZE = 5;

type ProcessingStatus = 'processed' | 'failed' | 'pending';

interface AlertRow {
  alert: Alert;
  processing: ProcessingStatus;
  processedAt?: Date;
}

@Component({
  selector: 'app-page-settings-alert-sources-details',
  templateUrl: 'details.component.html',
  imports: [
    SharedModule,
    RouterLink,
    CheckRoleDirective,
    DateAgoPipe,
    AlertStatusLabelComponent,
    CopyValueComponent,
    VxButtonComponent,
    VxCalloutComponent,
    VxCardComponent,
    VxCardHeaderComponent,
    VxCellDirective,
    VxDataTableComponent,
    VxHeaderDirective,
    VxIconComponent,
    VxIconButtonComponent,
    VxItemCellComponent,
    VxMenuComponent,
    VxMenuItemComponent,
    VxMenuTriggerDirective,
    VxPanelComponent,
    VxRowActionsDirective,
    VxSkeletonComponent,
    VxStatusDotComponent,
    VxTooltipDirective,
  ],
  host: { class: 'block' },
})
export class AlertSourcesDetailsSettingsPageComponent {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private breadcrumbs = inject(BreadcrumbService);
  private translate = inject(TranslocoService);
  private readonly confirmService = inject(ConfirmService);
  private sourceId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('id')))
  );

  private sourceEntity = this.store.selectSignal(
    AlertsState.selectedAlertSource
  );

  private alertsState = this.store.selectSignal(
    AlertsState.selectedAlertSourceAlerts
  );

  protected source = computed(() => this.sourceEntity().data);

  protected isLoading = computed(
    () => !this.source() && (this.sourceEntity().isLoading ?? true)
  );

  protected type = computed(() => {
    const source = this.source();
    return source ? alertSourceTypeVM(source.type) : null;
  });

  protected isManaged = computed(
    () => this.source()?.type === AlertSourceType.INCIDITE
  );

  protected page = signal(1);
  protected pageSize = signal(DEFAULT_PAGE_SIZE);

  /** Newest first — the slice keys entities by id, so order is re-established here. */
  protected rows = computed<AlertRow[]>(() => {
    const entities = this.alertsState()?.entities;
    if (!entities) {
      return [];
    }
    return SortEntities(entities, 'startedAt', 'DESC')
      .map(entity => entity.data)
      .filter((alert): alert is Alert => !!alert)
      .map(alert => ({
        alert,
        processing: !alert.processingStatus
          ? 'pending'
          : alert.processingStatus.error
            ? 'failed'
            : 'processed',
        processedAt: alert.processingStatus?.processedAt,
      }));
  });

  protected alertsLoading = computed(
    () => this.alertsState()?.isLoading === true
  );

  protected totalRecords = computed(
    () => this.alertsState()?.pagination?.totalRecords ?? 0
  );

  protected columns: VxColumn[] = [
    {
      key: 'alert',
      width: '3fr',
    },
    {
      key: 'status',
      width: '1fr',
    },
    {
      key: 'processing',
      width: '1.4fr',
    },
    {
      key: 'started',
      width: '1fr',
      align: 'end',
    },
  ];

  protected rangeLabel = computed(() => {
    const total = this.totalRecords();
    if (total === 0) {
      return '';
    }
    const first = (this.page() - 1) * this.pageSize() + 1;
    const last = Math.min(this.page() * this.pageSize(), total);
    return this.translate.translate('components.pagination.range', {
      first,
      last,
      total,
    });
  });

  constructor() {
    this.initLoading();
    this.initBreadcrumb();
  }

  private initLoading(): void {
    effect(() => {
      const id = this.sourceId();
      if (!id) {
        return;
      }
      this.store.dispatch(new LoadAlertSourceDetailsAction(id));
    });
    effect(() => {
      const id = this.sourceId();
      const page = this.page();
      const pageSize = this.pageSize();
      if (!id) {
        return;
      }
      this.store.dispatch(
        new LoadAlertSourceAlertsAction(id, { page, pageSize })
      );
    });
  }

  private initBreadcrumb(): void {
    effect(() => {
      this.breadcrumbs.setPageTitle(this.route, this.source()?.name || '');
    });
  }

  protected rowKey = (row: AlertRow) => row.alert.id;

  protected alertLink = (row: AlertRow) => ['/alerts', row.alert.id];

  protected onPageChanged(event: VxPage): void {
    this.page.set(event.index);
    this.pageSize.set(event.size);
  }

  protected openAlert(row: AlertRow): void {
    this.router.navigate(this.alertLink(row));
  }

  protected deleteSource(): void {
    const source = this.source();
    if (!source) {
      return;
    }

    const ns = 'pages.settings.alerting.sources.details.deletionDialog.alert';
    this.confirmService
      .confirm({
        title: `${ns}.header`,
        description: `${ns}.description`,
        confirmLabel: `${ns}.confirm`,
        tone: 'danger',
        icon: 'trash',
      })
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.store.dispatch(new DeleteAlertSourceAction(source.id)).subscribe({
          next: () => this.router.navigate(['/settings/alerting']),
        });
      });
  }
}
